import React, { useEffect, useMemo, useRef, useState } from "react";
import AppShell from "../../components/layout/AppShell";
import { api, formatApiError } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { toast } from "sonner";
import { Plus, PencilSimple, Trash, X, UserCircle, ArrowCounterClockwise, DownloadSimple, UploadSimple, CheckSquare, Square } from "@phosphor-icons/react";
import { PasswordConfirmModal } from "../admin/OfficeStaffList";

const ROLE_STYLE = {
  field_staff: "bg-[#D4E0D1] text-[#1A3317]",
  supervisor: "bg-[#F4C7B9] text-[#7A2E18]",
  // Legacy rendering
  collector: "bg-[#D4E0D1] text-[#1A3317]",
  driver: "bg-[#F9E4B7] text-[#7A5A11]",
};
const STATUS_STYLE = {
  active: "bg-[#D4E0D1] text-[#1A3317]",
  on_duty: "bg-[#2D5A27] text-white",
  inactive: "bg-[#E5E2D9] text-[#5C5A55]",
};

export default function StaffList() {
  const { user } = useAuth();
  const isSuper = user?.role === "superadmin";
  const [items, setItems] = useState([]);
  const [zones, setZones] = useState([]);
  const [showDeleted, setShowDeleted] = useState(false);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [confirmBulk, setConfirmBulk] = useState(false);
  const fileRef = useRef(null);
  const [form, setForm] = useState({
    name: "", phone: "", email: "", role: "field_staff",
    zone_id: "", device_id: "", status: "active",
    hourly_rate: 0, base_salary: 0, daily_hours: 8,
    address: "", emergency_contact: "", date_of_joining: "", staff_code: "",
  });

  const load = async () => {
    try {
      const [s, z] = await Promise.all([
        api.get(`/staff?include_deleted=${showDeleted ? "true" : "false"}`),
        api.get("/zones"),
      ]);
      setItems(s.data); setZones(z.data);
      setSelected(new Set());
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [showDeleted]);

  const openNew = () => {
    setEditing(null);
    setForm({ name: "", phone: "", email: "", role: "field_staff",
              zone_id: "", device_id: "", status: "active",
              hourly_rate: 0, base_salary: 0, daily_hours: 8,
              address: "", emergency_contact: "", date_of_joining: "", staff_code: "" });
    setOpen(true);
  };
  const openEdit = (s) => {
    setEditing(s.id);
    setForm({
      ...s,
      email: s.email || "", zone_id: s.zone_id || "", device_id: s.device_id || "",
      hourly_rate: s.hourly_rate || 0, base_salary: s.base_salary || 0,
      daily_hours: s.daily_hours ?? 8,
      address: s.address || "", emergency_contact: s.emergency_contact || "",
      date_of_joining: s.date_of_joining || "", staff_code: s.staff_code || "",
    });
    setOpen(true);
  };
  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form,
        email: form.email || null,
        zone_id: form.zone_id || null,
        device_id: form.device_id || null,
        hourly_rate: Number(form.hourly_rate) || 0,
        base_salary: Number(form.base_salary) || 0,
        daily_hours: form.daily_hours === "" || form.daily_hours === null ? null : Number(form.daily_hours),
        address: form.address || null,
        emergency_contact: form.emergency_contact || null,
        date_of_joining: form.date_of_joining || null,
        staff_code: form.staff_code || null };
      if (editing) await api.put(`/staff/${editing}`, payload);
      else await api.post("/staff", payload);
      toast.success("Saved");
      setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const del = async (id) => {
    if (!window.confirm("Soft-delete this staff record? History is retained; can be restored later.")) return;
    try { await api.delete(`/staff/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const hardDel = async (id) => {
    if (!window.confirm("PERMANENTLY delete this staff record?\nThis cannot be undone. Only allowed when no pending tasks are assigned.")) return;
    try { await api.delete(`/staff/${id}?hard=true`); toast.success("Permanently deleted"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const restore = async (id) => {
    try { await api.post(`/staff/${id}/restore`); toast.success("Restored"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const exportCsv = async () => {
    try {
      const res = await api.get("/staff/export.csv", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = "staff-export.csv"; a.click();
      URL.revokeObjectURL(url);
      toast.success("Export downloaded");
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const importCsv = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    try {
      const res = await api.post("/staff/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const { created, updated, skipped } = res.data;
      toast.success(`Import: ${created} created, ${updated} updated, ${skipped} skipped`);
      if (fileRef.current) fileRef.current.value = "";
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const activeItems = useMemo(() => items.filter(i => !i.deleted_at), [items]);
  const allSelected = activeItems.length > 0 && activeItems.every(r => selected.has(r.id));
  const toggleAll = () => {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(activeItems.map(r => r.id)));
  };
  const toggleOne = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };

  return (
    <AppShell title="Field Staff" subtitle="Staff Management"
      actions={
        <div className="flex items-center gap-2">
          <label className="text-xs text-[#5C5A55] inline-flex items-center gap-1.5 cursor-pointer" data-testid="toggle-show-deleted">
            <input type="checkbox" checked={showDeleted} onChange={e => setShowDeleted(e.target.checked)}/>
            Show deleted
          </label>
          {isSuper && selected.size > 0 && (
            <button data-testid="btn-bulk-delete-field"
                    onClick={() => setConfirmBulk(true)}
                    className="inline-flex items-center gap-1.5 h-10 px-3 bg-[#C25D38] hover:bg-[#A94C2C] text-white rounded-md text-sm font-medium">
              <Trash size={14} weight="bold"/>Delete selected ({selected.size})
            </button>
          )}
          {isSuper && (
            <>
              <button data-testid="btn-staff-export" onClick={exportCsv}
                      className="inline-flex items-center gap-1.5 h-10 px-3 bg-white border border-[#E5E2D9] hover:bg-[#F7F6F2] text-[#2D5A27] rounded-md text-sm font-medium">
                <DownloadSimple size={14} weight="bold"/>Export CSV
              </button>
              <button data-testid="btn-staff-import" onClick={() => fileRef.current?.click()}
                      className="inline-flex items-center gap-1.5 h-10 px-3 bg-white border border-[#E5E2D9] hover:bg-[#F7F6F2] text-[#2D5A27] rounded-md text-sm font-medium">
                <UploadSimple size={14} weight="bold"/>Import CSV
              </button>
              <input ref={fileRef} type="file" accept=".csv" hidden onChange={importCsv} data-testid="staff-import-input"/>
            </>
          )}
          <button data-testid="btn-new-staff" onClick={openNew}
                  className="inline-flex items-center gap-2 h-10 px-4 bg-[#2D5A27] hover:bg-[#3A7033] text-white rounded-md text-sm font-medium">
            <Plus size={14} weight="bold"/>Add staff
          </button>
        </div>
      }
    >
      <div className="bg-white border border-[#E5E2D9] rounded-md">
        <table className="w-full text-sm" data-testid="staff-table">
          <thead>
            <tr className="bg-[#F7F6F2] text-left text-[11px] uppercase tracking-[0.2em] text-[#5C5A55]">
              {isSuper && (
                <th className="px-4 py-3 w-8">
                  <button onClick={toggleAll} data-testid="field-select-all"
                          className="text-[#2D5A27] hover:opacity-80">
                    {allSelected ? <CheckSquare size={16} weight="fill"/> : <Square size={16}/>}
                  </button>
                </th>
              )}
              <th className="px-6 py-3">Name</th>
              <th className="px-6 py-3">Code</th>
              <th className="px-6 py-3">Phone</th>
              <th className="px-6 py-3">Role</th>
              <th className="px-6 py-3">Status</th>
              <th className="px-6 py-3">Device</th>
              <th className="px-6 py-3">Completed</th>
              <th className="px-6 py-3">Pending</th>
              <th className="px-6 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={isSuper ? 10 : 9} className="px-6 py-12 text-center text-[#8A8780]">No staff yet.</td></tr>}
            {items.map((s, i) => (
              <tr key={s.id} className={i % 2 === 0 ? "bg-white" : "bg-[#FAFAF7]"}>
                {isSuper && (
                  <td className="px-4 py-3">
                    {!s.deleted_at && (
                      <button onClick={() => toggleOne(s.id)}
                              data-testid={`field-select-${s.id}`}
                              className="text-[#2D5A27] hover:opacity-80">
                        {selected.has(s.id) ? <CheckSquare size={16} weight="fill"/> : <Square size={16}/>}
                      </button>
                    )}
                  </td>
                )}
                <td className="px-6 py-3 flex items-center gap-2">
                  <UserCircle size={20} color={s.deleted_at ? "#8A8780" : "#2D5A27"} weight="duotone" />
                  <span className={`font-medium ${s.deleted_at ? "line-through text-[#8A8780]" : ""}`}>{s.name}</span>
                  {s.deleted_at && <span className="text-[9px] uppercase tracking-widest bg-[#F4C7B9] text-[#7A2E18] px-1.5 py-0.5 rounded">Deleted</span>}
                </td>
                <td className="px-6 py-3 font-mono text-xs text-[#5C5A55]">{s.staff_code || "—"}</td>
                <td className="px-6 py-3 font-mono text-xs text-[#5C5A55]">{s.phone}</td>
                <td className="px-6 py-3">
                  <span className={`px-2 py-0.5 text-[10px] uppercase tracking-[0.18em] rounded ${ROLE_STYLE[s.role] || "bg-[#E5E2D9]"}`}>{s.role}</span>
                </td>
                <td className="px-6 py-3">
                  <span className={`px-2 py-0.5 text-[10px] uppercase tracking-[0.18em] rounded ${STATUS_STYLE[s.status] || "bg-[#E5E2D9]"}`}>{s.status}</span>
                </td>
                <td className="px-6 py-3 font-mono text-xs text-[#8A8780]">{s.device_id || "—"}</td>
                <td className="px-6 py-3 font-mono">{s.tasks_completed || 0}</td>
                <td className="px-6 py-3 font-mono">{s.tasks_pending || 0}</td>
                <td className="px-6 py-3 text-right">
                  {s.deleted_at ? (
                    <>
                      <button data-testid={`staff-restore-${s.id}`} onClick={() => restore(s.id)} className="text-[#2D5A27] text-xs mr-3 inline-flex items-center gap-1"><ArrowCounterClockwise size={12}/>Restore</button>
                      {isSuper && (
                        <button data-testid={`staff-harddel-${s.id}`} onClick={() => hardDel(s.id)} className="text-[#C25D38] text-xs inline-flex items-center gap-1"><Trash size={12}/>Purge</button>
                      )}
                    </>
                  ) : (
                    <>
                      <a data-testid={`staff-profile-${s.id}`} href={`/workforce/staff/${s.id}`}
                          className="text-[#2D5A27] text-xs mr-3 inline-flex items-center gap-1">Profile</a>
                      <button data-testid={`staff-edit-${s.id}`} onClick={() => openEdit(s)} className="text-[#2D5A27] text-xs mr-3 inline-flex items-center gap-1"><PencilSimple size={12}/>Edit</button>
                      <button data-testid={`staff-del-${s.id}`} onClick={() => del(s.id)} className="text-[#C25D38] text-xs inline-flex items-center gap-1"><Trash size={12}/>Delete</button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="bg-white rounded-md w-full max-w-lg p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <div className="font-heading text-lg">{editing ? "Edit" : "New"} staff</div>
              <button onClick={() => setOpen(false)} className="p-1 hover:bg-[#F7F6F2] rounded"><X size={16}/></button>
            </div>
            <form onSubmit={submit} className="grid grid-cols-2 gap-4" data-testid="staff-form">
              <Field label="Name" value={form.name} onChange={v => setForm(f=>({...f,name:v}))} required testid="sf-name"/>
              <Field label="Phone" value={form.phone} onChange={v => setForm(f=>({...f,phone:v}))} required testid="sf-phone"/>
              <Field label="Email" value={form.email} onChange={v => setForm(f=>({...f,email:v}))}/>
              <Field label="Device ID" value={form.device_id} onChange={v => setForm(f=>({...f,device_id:v}))}/>
              <Select label="Role" value={form.role} onChange={v => setForm(f=>({...f,role:v}))}
                      options={[
                        {value:"field_staff",label:"Field Staff (mobile)"},
                        {value:"supervisor",label:"Supervisor (mobile)"},
                      ]} testid="sf-role"/>
              <Select label="Status" value={form.status} onChange={v => setForm(f=>({...f,status:v}))}
                      options={["active","on_duty","inactive"].map(r=>({value:r,label:r}))}/>
              <Select label="Zone" value={form.zone_id} onChange={v => setForm(f=>({...f,zone_id:v}))}
                      options={[{value:"",label:"— none —"}, ...zones.map(z=>({value:z.id,label:z.name}))]}/>
              <Field label="Staff Code" value={form.staff_code} onChange={v => setForm(f=>({...f,staff_code:v}))}/>
              <Field label="Hourly Rate (₹/hr)" value={form.hourly_rate} onChange={v => setForm(f=>({...f,hourly_rate:v}))} testid="sf-hourly"/>
              <Field label="Base Salary (₹/mo)" value={form.base_salary} onChange={v => setForm(f=>({...f,base_salary:v}))} testid="sf-base"/>
              <Field label="Daily Duty Hours" value={form.daily_hours} onChange={v => setForm(f=>({...f,daily_hours:v}))} testid="sf-daily-hours"/>
              <Field label="Date of Joining" value={form.date_of_joining} onChange={v => setForm(f=>({...f,date_of_joining:v}))}/>
              <Field label="Emergency Contact" value={form.emergency_contact} onChange={v => setForm(f=>({...f,emergency_contact:v}))}/>
              <div className="col-span-2">
                <label className="text-[11px] uppercase tracking-[0.2em] text-[#5C5A55] block mb-2">Address</label>
                <textarea value={form.address} onChange={e => setForm(f=>({...f,address:e.target.value}))} rows={2}
                          className="w-full px-3 py-2 border border-[#E5E2D9] rounded-md text-sm"/>
              </div>
              <button data-testid="sf-save" type="submit" className="col-span-2 h-10 bg-[#2D5A27] hover:bg-[#3A7033] text-white rounded-md text-sm font-medium">Save</button>
            </form>
          </div>
        </div>
      )}
      {confirmBulk && (
        <PasswordConfirmModal
          count={selected.size}
          label="field staff"
          onClose={() => setConfirmBulk(false)}
          onConfirm={async (password) => {
            try {
              const r = await api.post("/staff/bulk-delete", {
                ids: Array.from(selected), password,
              });
              const n = r.data.soft_deleted ?? r.data.hard_deleted ?? 0;
              if (n === 0) {
                // Server accepted the request but matched 0 rows — surface
                // it loudly so the user doesn't stare at an unchanged list.
                toast.error(
                  "0 staff were deleted — the selected records may already be soft-deleted or belong to another tenant. Refresh the page and try again.",
                  { duration: 6000 },
                );
              } else {
                toast.success(`Deleted ${n} staff`);
              }
              setConfirmBulk(false);
              setSelected(new Set());
              // Iter 87 — small delay so the write is definitely visible
              // when the list re-fetches on read replicas / prod DB.
              setTimeout(load, 120);
            } catch (e) { toast.error(formatApiError(e)); }
          }}
        />
      )}
    </AppShell>
  );
}

const Field = ({ label, value, onChange, required, testid }) => (
  <div>
    <label className="text-[11px] uppercase tracking-[0.2em] text-[#5C5A55] block mb-2">{label}</label>
    <input data-testid={testid} required={required} value={value}
           onChange={e => onChange(e.target.value)}
           className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md"/>
  </div>
);
const Select = ({ label, value, onChange, options, testid }) => (
  <div>
    <label className="text-[11px] uppercase tracking-[0.2em] text-[#5C5A55] block mb-2">{label}</label>
    <select data-testid={testid} value={value} onChange={e => onChange(e.target.value)}
            className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md bg-white">
      {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  </div>
);
